import React, { useState, useEffect } from 'react';
import { apiClient } from '../utils/apiClient';

const BASE = process.env.REACT_APP_API_BASE_URL;

const NIVEAUX = [
    { group: 'Collège',    options: ['6ème', '5ème', '4ème', '3ème'] },
    { group: 'Lycée',      options: ['Seconde', 'Première', 'Terminale'] },
    { group: 'Supérieur',  options: ['CPGE 1', 'CPGE 2', 'BTS', 'Licence 1', 'Licence 2', 'Licence 3', 'Master'] },
];

const INPUT = "w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500";

function Section({ title, children }) {
    return (
        <div className="py-5 border-t border-gray-200 dark:border-gray-700 first:border-t-0 first:pt-0">
            {title && <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4">{title}</p>}
            {children}
        </div>
    );
}

function ProfileSettings({ user, isDarkMode, toggleDarkMode, onLogout, onProfileUpdate, embedded = false }) {
    const [parentCode, setParentCode] = useState(null);
    const [codeCopied, setCodeCopied] = useState(false);
    const [mesClasses, setMesClasses] = useState([]);
    const [classeCode, setClasseCode] = useState('');
    const [classeErr, setClasseErr]   = useState('');
    const [classeOk, setClasseOk]     = useState('');
    const [joining, setJoining]       = useState(false);

    // Profil éditable
    const [profile, setProfile]     = useState({ first_name: '', last_name: '', birth_date: '', niveau: '', email: '' });
    const [profileOrig, setProfileOrig] = useState(null);
    const [saving, setSaving]       = useState(false);
    const [saveOk, setSaveOk]       = useState('');
    const [saveErr, setSaveErr]     = useState('');

    const isDirty = profileOrig && (
        profile.first_name !== profileOrig.first_name ||
        profile.last_name  !== profileOrig.last_name  ||
        profile.birth_date !== (profileOrig.birth_date || '') ||
        profile.niveau     !== (profileOrig.niveau || '')
    );

    useEffect(() => {
        if (user.role === 'etudiant') {
            apiClient(`${BASE}/parent_dashboard.php?action=my_code`)
                .then(r => r.json())
                .then(j => { if (j.success) setParentCode(j.code); })
                .catch(() => {});
            apiClient(`${BASE}/classe_invites.php?action=my_classes`)
                .then(r => r.json())
                .then(j => { if (j.success) setMesClasses(j.classes); })
                .catch(() => {});
        }
        // Charger le profil pour tous les rôles
        apiClient(`${BASE}/profile_api.php?action=get`)
            .then(r => r.json())
            .then(j => {
                if (j.success) {
                    const p = {
                        first_name: j.profile.first_name || '',
                        last_name:  j.profile.last_name  || '',
                        birth_date: j.profile.birth_date || '',
                        niveau:     j.profile.niveau     || '',
                        email:      j.profile.email      || '',
                    };
                    setProfile(p);
                    setProfileOrig(p);
                }
            })
            .catch(() => {});
    }, [user.role]);

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        setSaveErr('');
        setSaveOk('');
        if (!profile.first_name.trim()) { setSaveErr('Le prénom est requis.'); return; }
        setSaving(true);
        try {
            const res  = await apiClient(`${BASE}/profile_api.php?action=update`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(profile),
            });
            const data = await res.json();
            if (data.success) {
                setSaveOk('Profil mis à jour.');
                setProfileOrig({ ...profile });
                onProfileUpdate?.(profile.first_name.trim(), data.name);
                setTimeout(() => setSaveOk(''), 3000);
            } else {
                setSaveErr(data.message || 'Erreur lors de la mise à jour.');
            }
        } catch {
            setSaveErr('Erreur de connexion au serveur.');
        } finally {
            setSaving(false);
        }
    };

    const handleJoinClasse = async (e) => {
        e.preventDefault();
        setClasseErr('');
        setClasseOk('');
        const trimmed = classeCode.trim().toUpperCase();
        if (!trimmed) return;
        setJoining(true);
        try {
            const res  = await apiClient(`${BASE}/classe_invites.php?action=join_by_code`, {
                method: 'POST',
                body: JSON.stringify({ code: trimmed }),
            });
            const json = await res.json();
            if (json.success) {
                setClasseOk(`Vous avez rejoint la classe « ${json.classe_nom} » !`);
                setClasseCode('');
            } else {
                setClasseErr(json.message || 'Code invalide.');
            }
        } finally { setJoining(false); }
    };

    const copyCode = () => {
        if (!parentCode) return;
        navigator.clipboard.writeText(parentCode);
        setCodeCopied(true);
        setTimeout(() => setCodeCopied(false), 2000);
    };

    const handleThemeToggle = () => {
        toggleDarkMode();
        apiClient(`${BASE}/update_theme.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ dark: !isDarkMode }),
        })
        .then(res => res.json())
        .then(data => { if (data.success && data.token) localStorage.setItem('token', data.token); })
        .catch(err => console.error('Erreur sauvegarde thème :', err));
    };

    return (
        <div className={embedded ? '' : 'min-h-screen bg-gray-50 dark:bg-gray-950 p-6'}>
            <div className={embedded ? '' : 'max-w-4xl mx-auto mt-10'}>
                <div className="bg-white/70 dark:bg-white/[0.03] backdrop-blur-xl rounded-3xl shadow-2xl p-8 md:p-12 border border-gray-200 dark:border-white/[0.08]">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Paramètres du Profil</h1>

                    <div className="space-y-0">

                        {/* ── Identité ─────────────────────────────────────── */}
                        <Section title="Identité">
                            <form onSubmit={handleSaveProfile} className="flex flex-col gap-4">
                                <div className="flex gap-3">
                                    <div className="flex-1">
                                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Prénom *</label>
                                        <input type="text" value={profile.first_name}
                                            onChange={e => setProfile(p => ({ ...p, first_name: e.target.value }))}
                                            className={INPUT} required />
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Nom</label>
                                        <input type="text" value={profile.last_name}
                                            onChange={e => setProfile(p => ({ ...p, last_name: e.target.value }))}
                                            className={INPUT} />
                                    </div>
                                </div>

                                <div className="flex gap-3">
                                    <div className="flex-1">
                                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Date de naissance</label>
                                        <input type="date" value={profile.birth_date}
                                            onChange={e => setProfile(p => ({ ...p, birth_date: e.target.value }))}
                                            className={INPUT} />
                                    </div>
                                    {user.role === 'etudiant' && (
                                        <div className="flex-1">
                                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Niveau / classe</label>
                                            <select value={profile.niveau}
                                                onChange={e => setProfile(p => ({ ...p, niveau: e.target.value }))}
                                                className={INPUT}>
                                                <option value="">— Non renseigné —</option>
                                                {NIVEAUX.map(({ group, options }) => (
                                                    <optgroup key={group} label={group}>
                                                        {options.map(o => <option key={o} value={o}>{o}</option>)}
                                                    </optgroup>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>

                                {profile.email && (
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Email</label>
                                        <p className="text-sm text-gray-600 dark:text-gray-300 px-3 py-2 bg-gray-50 dark:bg-white/5 rounded-xl border border-gray-200 dark:border-white/5">
                                            {profile.email}
                                        </p>
                                    </div>
                                )}

                                {saveErr && <p className="text-sm text-red-500 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">{saveErr}</p>}
                                {saveOk  && <p className="text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-lg px-3 py-2">{saveOk}</p>}

                                <div className="flex justify-end">
                                    <button type="submit" disabled={saving || !isDirty}
                                        className="px-5 py-2 rounded-xl bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors">
                                        {saving ? 'Enregistrement…' : 'Enregistrer'}
                                    </button>
                                </div>
                            </form>
                        </Section>

                        {/* ── Classes — élèves uniquement ───────────────────── */}
                        {user.role === 'etudiant' && (
                            <Section title="Classes">
                                {mesClasses.length === 0 ? (
                                    <p className="text-sm text-gray-400 mb-4">Aucune classe pour l'instant.</p>
                                ) : (
                                    <div className="flex flex-col gap-2 mb-4">
                                        {mesClasses.map((c, i) => (
                                            <div key={i} className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-teal-50 dark:bg-teal-900/20 border border-teal-100 dark:border-teal-500/20">
                                                <div className="w-2 h-2 rounded-full bg-teal-500 shrink-0" />
                                                <div>
                                                    <p className="text-sm font-semibold text-teal-800 dark:text-teal-200">{c.classe_nom}</p>
                                                    <p className="text-xs text-teal-600 dark:text-teal-400">{c.professeur_name}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Rejoindre une classe</label>
                                {classeOk && <p className="text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-lg px-3 py-2 mb-3">{classeOk}</p>}
                                <form onSubmit={handleJoinClasse} className="flex items-start gap-3 flex-wrap">
                                    <div className="flex flex-col gap-1">
                                        <input type="text" placeholder="Code de classe" value={classeCode}
                                            onChange={e => { setClasseCode(e.target.value.toUpperCase()); setClasseErr(''); }}
                                            maxLength={8}
                                            className="px-3 py-2 rounded-xl border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm font-mono tracking-widest placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 w-44" />
                                        {classeErr && <p className="text-xs text-red-500">{classeErr}</p>}
                                    </div>
                                    <button type="submit" disabled={joining}
                                        className="px-4 py-2 rounded-xl bg-teal-600 hover:bg-teal-500 disabled:opacity-50 text-white text-sm font-semibold transition-colors">
                                        {joining ? 'Recherche…' : 'Rejoindre'}
                                    </button>
                                </form>
                            </Section>
                        )}

                        {/* ── Code parent ───────────────────────────────────── */}
                        {user.role === 'etudiant' && parentCode && (
                            <Section title="Code parent">
                                <p className="text-xs text-gray-400 mb-3">Partagez ce code avec vos parents pour qu'ils créent leur espace de suivi.</p>
                                <div className="flex items-center gap-3">
                                    <span className="font-mono text-xl font-bold tracking-[0.3em] text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 px-4 py-2 rounded-xl border border-orange-200 dark:border-orange-500/30 select-all">
                                        {parentCode}
                                    </span>
                                    <button onClick={copyCode}
                                        className="px-3 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                                        {codeCopied ? '✓ Copié' : 'Copier'}
                                    </button>
                                </div>
                            </Section>
                        )}

                        {/* ── Apparence ─────────────────────────────────────── */}
                        <Section title="Apparence">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Mode Sombre</span>
                                <button onClick={handleThemeToggle}
                                    className="px-4 py-2 rounded-full bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-200 shadow-md hover:scale-105 transition-all duration-300 text-sm">
                                    {isDarkMode ? '☀️ Mode Clair' : '🌙 Mode Sombre'}
                                </button>
                            </div>
                        </Section>

                        {/* ── Déconnexion ───────────────────────────────────── */}
                        <Section>
                            <div className="flex justify-end">
                                <button onClick={onLogout}
                                    className="px-6 py-2 rounded-xl text-sm font-semibold text-red-600 dark:text-red-400 border border-red-500/30 hover:bg-red-500/10 transition-all duration-300">
                                    Déconnexion
                                </button>
                            </div>
                        </Section>

                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProfileSettings;
